import React, { useState, useEffect } from 'react';
import { useJazyk } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getAvatarImage } from '../avatarSystem';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Save, Lock, Globe, Users, ChevronRight, Heart } from 'lucide-react';
import { API } from '../../lib/api';
import { STAGES } from '../../lib/journeyStages';

export function ProfileJourney() {
  const { t } = useJazyk();
  const [journeyStage, setJourneyStage] = useState('');
  const [journeyPublic, setJourneyPublic] = useState(false);
  const [journeyNote, setJourneyNote] = useState('');
  const [journeySaving, setJourneySaving] = useState(false);
  const [similarUsers, setSimilarUsers] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);

  useEffect(() => {
    axios.get(`${API}/users/me`).then(r => {
      const journey = r.data?.journey;
      if (journey) {
        setJourneyStage(journey.stage || '');
        setJourneyPublic(journey.is_public || false);
        setJourneyNote(journey.note || '');
      }
    }).catch(() => {});
  }, []);

  const handleSaveJourney = async () => {
    if (!journeyStage) { toast.error(t('cesta.vyberteFazi')); return; }
    setJourneySaving(true);
    const stageData = STAGES.find(s => s.id === journeyStage);
    try {
      await axios.put(`${API}/users/me/journey`, {
        stage: journeyStage, stage_label: stageData?.label || journeyStage,
        is_public: journeyPublic, note: journeyNote
      });
      toast.success(t('cesta.ulozeno'));
      if (journeyPublic) fetchSimilarUsers();
    } catch { toast.error(t('cesta.chyba')); }
    finally { setJourneySaving(false); }
  };

  const fetchSimilarUsers = async () => {
    setLoadingSimilar(true);
    try { const r = await axios.get(`${API}/journey/similar`); setSimilarUsers(r.data); }
    catch {} finally { setLoadingSimilar(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-white border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-bloom-text flex items-center gap-2"><Heart className="w-4 h-4 text-bloom-violet" />{t('cesta.kdeJsem')}</CardTitle>
          <p className="text-xs text-bloom-sub">{t('cesta.popis')}</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {STAGES.map(stage => (
              <button key={stage.id} onClick={() => setJourneyStage(stage.id)}
                className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all hover:shadow-sm ${journeyStage === stage.id ? 'border-bloom-violet/60 shadow-sm bg-bloom-violet/5' : 'border-border/50 bg-white hover:border-bloom-violet/30'}`}
                data-testid={`stage-${stage.id}`}>
                <div className="w-4 h-4 rounded-full shrink-0 ring-2 ring-white shadow-sm" style={{ background: stage.color, boxShadow: journeyStage === stage.id ? `0 0 0 3px ${stage.color}50` : undefined }} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold truncate ${journeyStage === stage.id ? 'text-bloom-violet' : 'text-bloom-text'}`}>{t(stage.klic)}</p>
                  {journeyStage === stage.id && <p className="text-[10px] text-bloom-sub mt-0.5 leading-relaxed line-clamp-2">{t(stage.klicPopis)}</p>}
                </div>
                <Link to={`/journey/${stage.id}`} onClick={e => e.stopPropagation()}
                  className="text-[10px] font-medium text-bloom-violet hover:underline shrink-0 whitespace-nowrap"
                  data-testid={`stage-guide-link-${stage.id}`}>
                  {t('cesta.coMeCeka')}
                </Link>
                {journeyStage === stage.id && <ChevronRight className="w-3.5 h-3.5 text-bloom-violet shrink-0" />}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-bloom-text">{t('cesta.poznamka')} <span className="text-bloom-sub font-normal">{t('cesta.volitelna')}</span></Label>
        <Textarea value={journeyNote} onChange={e => setJourneyNote(e.target.value)} placeholder={t('cesta.poznamkaPriklad')} rows={3} className="text-sm" data-testid="journey-note-input" />
      </div>

      <Card className="bg-white border-border/50">
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {journeyPublic ? <Globe className="w-4 h-4 text-bloom-violet" /> : <Lock className="w-4 h-4 text-bloom-sub" />}
            <div>
              <p className="text-sm font-medium text-bloom-text">{journeyPublic ? t('cesta.viditelna') : t('cesta.soukroma')}</p>
              <p className="text-xs text-bloom-sub">{journeyPublic ? t('cesta.mohouNajit') : t('cesta.jenTy')}</p>
            </div>
          </div>
          <Switch checked={journeyPublic} onCheckedChange={setJourneyPublic} data-testid="journey-public-toggle" />
        </CardContent>
      </Card>

      <Button onClick={handleSaveJourney} className="w-full bg-bloom-violet text-white" disabled={journeySaving || !journeyStage} data-testid="save-journey-btn">
        <Save className="w-4 h-4 mr-2" />{journeySaving ? t('pf.ukladam') : t('cesta.ulozit')}
      </Button>

      {journeyStage && (
        <Card className="bg-gradient-to-br from-bloom-violet/5 to-[#5BCEFA]/5 border-bloom-violet/20">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-bloom-text flex items-center gap-2"><Users className="w-4 h-4 text-bloom-violet" />{t('cesta.najitPodobne')}</p>
              <Button size="sm" variant="outline" className="border-bloom-violet/30 text-bloom-violet" onClick={fetchSimilarUsers} disabled={loadingSimilar} data-testid="find-similar-btn">
                {loadingSimilar ? t('cesta.hledam') : 'Hledat'}
              </Button>
            </div>
            {similarUsers.length > 0 && (
              <div className="space-y-2">
                {similarUsers.map(u => (
                  <Link key={u.id} to={`/users/${u.id}`} className="flex items-center gap-2.5 p-2.5 bg-white rounded-lg hover:shadow-sm transition-shadow" data-testid={`similar-user-${u.id}`}>
                    <Avatar className="w-8 h-8"><AvatarImage src={getAvatarImage(u.avatar)} /><AvatarFallback className="bg-bloom-violet text-white text-xs">{u.username?.charAt(0)}</AvatarFallback></Avatar>
                    <div><p className="text-sm font-medium text-bloom-text">{u.username}</p>{u.location && <p className="text-xs text-bloom-sub">{u.location}</p>}</div>
                    <ChevronRight className="w-3.5 h-3.5 text-bloom-sub ml-auto" />
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
